import React, { useEffect, useRef, useState } from 'react';
import { useTicketMetricAdmin, useUpdateTicketMetricAdmin, useTestTicketMetricAdmin, tmErrorText } from '../hooks/useTicketMetric';
import { formatDateTimeEastern, formatDateEastern, formatEventDateOnly, timeAgo } from '../utils/dates';

// Admin → TicketMetric (super admin only): the program's own TicketMetric
// login and proxy (stored encrypted with SKYBOX_CRED_KEY), a connection test,
// and the live queue status. Password and proxy URL are write-only: the
// server only says whether they are set (the proxy as scheme://host:port).

const WARNING_TEXT = {
  KEY_MISSING: "SKYBOX_CRED_KEY is not set on the server — the login can't be saved",
  NO_PROXY: "No proxy — TicketMetric sees the server's own IP",
  SECRETS_UNREADABLE: "The saved login can't be decrypted (was SKYBOX_CRED_KEY changed?) — enter the password and proxy again",
};
const PRIORITY_TEXT = { 0: 'interactive', 1: 'compare', 2: 'background' };
const TEST_WAIT_MAX_MS = 3 * 60 * 1000;

const when = (v) => (v ? formatDateTimeEastern(v, { year: undefined }) : '-');

// Plan expiry may come as a date, an ISO instant or epoch seconds/ms
function fmtExpiry(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return formatDateEastern(v < 1e12 ? v * 1000 : v);
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return formatEventDateOnly(s);
  const out = formatDateEastern(s);
  return out === '-' ? s : out;
}

// The Test result → checklist rows { state: ok|bad|warn|skip, label, text }
function testChecks(t) {
  if (!t) return [];
  if (!t.login) return [{ state: 'bad', label: 'Test', text: t.error || 'The test did not run' }];
  const rows = [];
  const p = t.proxy || {};
  if (p.configured) {
    rows.push(p.exitIp
      ? { state: 'ok', label: 'Proxy', text: `exits at ${p.exitIp}${p.display ? ` (via ${p.display})` : ''}` }
      : { state: 'bad', label: 'Proxy', text: `no exit IP — ${p.exitIpError || 'the proxy did not answer'}` });
  } else {
    rows.push({ state: 'warn', label: 'Proxy', text: `none — direct connection${p.exitIp ? `; TicketMetric sees ${p.exitIp}` : ''}` });
  }
  const loginOk = !!t.login.ok;
  rows.push(loginOk
    ? { state: 'ok', label: 'Login', text: 'logged in' }
    : { state: 'bad', label: 'Login', text: t.login.error || 'failed' });
  if (!loginOk) {
    for (const label of ['Pro plan', 'Search', 'Sold history']) rows.push({ state: 'skip', label, text: 'not checked (login failed)' });
    return rows;
  }
  const plan = t.plan;
  if (!plan || plan.pro == null) rows.push({ state: 'bad', label: 'Pro plan', text: plan?.error || 'could not read the plan' });
  else if (plan.pro) {
    const exp = fmtExpiry(plan.expires);
    rows.push({ state: 'ok', label: 'Pro plan', text: `${plan.name || 'Pro'}${exp ? ` · expires ${exp}` : ''}` });
  } else {
    rows.push({ state: 'bad', label: 'Pro plan', text: `no Pro plan${plan.name ? ` (${plan.name})` : ''} — search and sold history need SalesTIQ Pro` });
  }
  const sr = t.search || {};
  rows.push(sr.ok
    ? { state: 'ok', label: 'Search', text: `answers (${sr.records} record${sr.records === 1 ? '' : 's'})` }
    : { state: 'bad', label: 'Search', text: sr.error || 'failed' });
  const so = t.sold || {};
  rows.push(so.ok
    ? { state: 'ok', label: 'Sold history', text: `answers (${so.rows} row${so.rows === 1 ? '' : 's'}${so.tmetricId ? ` for event ${so.tmetricId}` : ''})` }
    : { state: 'bad', label: 'Sold history', text: so.error || 'failed' });
  return rows;
}

const MARK = { ok: '✓', bad: '✗', warn: '!', skip: '–' };

// Plan expiry as epoch ms (a date, an ISO instant or epoch seconds/ms), or null
function expiryMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number' || /^\d+$/.test(String(v))) { const n = Number(v); return n < 1e12 ? n * 1000 : n; }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}
const EXPIRY_WARN_MS = 14 * 86400000;

const SKIP_TEXT = {
  off: 'off',
  not_configured: 'not connected',
  queue_busy: 'queue busy (over 20 background jobs)',
  endpoint_blocked: 'an endpoint is blocked',
  paused: 'TicketMetric asked us to slow down',
  plan_not_pro: 'plan is not Pro',
};

function TicketMetricAdminTab({ onMessage }) {
  // While a long test runs in the background (202), poll faster for its result
  const [waitingTest, setWaitingTest] = useState(null); // { prevAt } | null
  const { data, isLoading, error } = useTicketMetricAdmin({ refetchInterval: waitingTest ? 3000 : 10000 });
  const save = useUpdateTicketMetricAdmin();
  const test = useTestTicketMetricAdmin();

  const [email, setEmail] = useState(null); // null = untouched → the saved email
  const [password, setPassword] = useState('');
  const [proxyUrl, setProxyUrl] = useState('');
  const [pool, setPool] = useState(null);   // null = untouched → the saved value
  const [testResult, setTestResult] = useState(null);
  // Admin passes a fresh onMessage every render; the timer below must not reset on that
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  // A 202 test finishes in the background; it lands as lastTest
  const lastTest = data?.lastTest || null;
  useEffect(() => {
    if (!waitingTest) return;
    if (lastTest?.at && lastTest.at !== waitingTest.prevAt) {
      setTestResult(lastTest);
      setWaitingTest(null);
    }
  }, [lastTest, waitingTest]);
  useEffect(() => {
    if (!waitingTest) return undefined;
    const t = setTimeout(() => {
      setWaitingTest(null);
      onMessageRef.current?.('The test is still running — its result shows here once it finishes', true);
    }, TEST_WAIT_MAX_MS);
    return () => clearTimeout(t);
  }, [waitingTest]);

  if (isLoading) return <div className="loading">Loading TicketMetric settings…</div>;
  if (error || !data) return <div className="message message-error">{tmErrorText(error, 'Failed to load the TicketMetric settings')}</div>;

  const cfg = data.config || {};
  const st = data.status || {};
  const client = st.client || {};
  const queue = st.queue || {};
  const warnings = cfg.warnings || [];
  const keyMissing = warnings.includes('KEY_MISSING') || cfg.keyPresent === false;

  const emailValue = email ?? cfg.email ?? '';
  const poolValue = pool ?? String(cfg.poolSiblings ?? 4);
  const poolNum = Number(poolValue);
  const poolValid = poolValue !== '' && Number.isInteger(poolNum) && poolNum >= 0 && poolNum <= 10;

  const body = {};
  if (emailValue.trim() && emailValue.trim() !== (cfg.email || '')) body.email = emailValue.trim();
  if (password) body.password = password;
  if (proxyUrl.trim()) body.proxyUrl = proxyUrl.trim();
  if (pool !== null && poolValid && poolNum !== cfg.poolSiblings) body.poolSiblings = poolNum;
  const dirty = Object.keys(body).length > 0;
  const credsChanged = !!(body.email || body.password || body.proxyUrl);

  const doSave = (payload, okText) => {
    save.mutate(payload, {
      onSuccess: () => {
        setPassword('');
        setProxyUrl('');
        setEmail(null);
        setPool(null);
        onMessage?.(okText);
      },
      onError: (err) => onMessage?.(tmErrorText(err, 'Failed to save the TicketMetric settings'), true),
    });
  };
  const onSave = (e) => {
    e.preventDefault();
    if (!dirty || !poolValid) return;
    doSave(body, credsChanged ? 'TicketMetric settings saved — run Test connection to check them' : 'TicketMetric settings saved');
  };
  const removeProxy = () => {
    if (!window.confirm("Remove the proxy? TicketMetric calls will then go out directly from the server's own IP.")) return;
    doSave({ clearProxy: true }, 'Proxy removed — TicketMetric now connects directly');
  };
  const runTest = async () => {
    setTestResult(null);
    const prevAt = lastTest?.at || null;
    try {
      const r = await test.mutateAsync();
      if (r?.status === 'done') setTestResult(r.result || null);
      else setWaitingTest({ prevAt });
    } catch (err) {
      onMessage?.(tmErrorText(err, 'The TicketMetric test failed to run'), true);
    }
  };

  const shownTest = testResult || lastTest;
  const checks = testChecks(shownTest);
  const testing = test.isPending || !!waitingTest;
  const plan = cfg.plan;
  const blocked = queue.blockedEndpoints || [];
  const recent = queue.recent || [];
  const dp = st.dailyPull || null;
  const expMs = expiryMs(plan?.expires);
  const expiringSoon = expMs != null && expMs - Date.now() < EXPIRY_WARN_MS;
  const toggleDailyPull = (on) => {
    save.mutate({ dailyPull: on }, {
      onSuccess: () => onMessage?.(on ? 'Daily pull on — held events are refreshed in the background' : 'Daily pull off'),
      onError: (err) => onMessage?.(tmErrorText(err, 'Failed to switch the daily pull'), true),
    });
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>TicketMetric</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className={`ai-status-pill ${cfg.configured ? 'on' : 'off'}`}>{cfg.configured ? 'CONNECTED' : 'NOT CONNECTED'}</span>
        </div>
      </div>

      {warnings.map(w => (
        <div key={w} className="message message-error" style={{ marginBottom: 12 }}>{WARNING_TEXT[w] || w}</div>
      ))}
      {expiringSoon && (
        <div className="message message-error" style={{ marginBottom: 12 }}>
          The TicketMetric plan {expMs < Date.now() ? 'expired' : 'expires'} {fmtExpiry(plan.expires)} — renew it, or sold data and the daily pull stop.
        </div>
      )}

      <div className="ai-admin-note" style={{ marginBottom: 8 }}>
        The pricing tool logs in to TicketMetric with this account (it needs the SalesTIQ Pro plan) and pulls sold data
        through the proxy below — one call at a time for the whole program. Pricers see it on Compare and the AI uses it.
      </div>

      <form onSubmit={onSave}>
        <div className="ai-admin-row">
          <label>Login email</label>
          <input type="text" value={emailValue} onChange={e => setEmail(e.target.value)} placeholder="TicketMetric login email" autoComplete="off" style={{ minWidth: 280 }} />
        </div>
        <div className="ai-admin-row">
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={cfg.hasPassword ? 'saved — leave blank to keep' : 'TicketMetric password'}
            autoComplete="new-password"
            style={{ minWidth: 280 }}
          />
        </div>
        <div className="ai-admin-row">
          <label>Proxy</label>
          <input
            type="text"
            value={proxyUrl}
            onChange={e => setProxyUrl(e.target.value)}
            placeholder={cfg.proxy ? `saved: ${cfg.proxy} — leave blank to keep` : 'host:port:username:password  (as your provider gives it)'}
            autoComplete="off"
            spellCheck={false}
            style={{ minWidth: 380 }}
          />
          {cfg.hasProxy && (
            <button type="button" className="btn-secondary btn-sm" onClick={removeProxy} disabled={save.isPending || keyMissing}>Remove proxy</button>
          )}
          <span className="ai-admin-note">Paste it exactly as your provider gives it (host:port:username:password), or a URL like http://user:pass@host:port. Write-only. Current: {cfg.proxy || (cfg.hasProxy ? 'saved (unreadable)' : 'none — direct connection')}</span>
        </div>
        <div className="ai-admin-row">
          <label>Sister dates to pool</label>
          <input type="number" min="0" max="10" step="1" value={poolValue} onChange={e => setPool(e.target.value)} style={{ width: 80 }} />
          <span className="ai-admin-note">
            The AI also uses sold data from up to this many nearest dates of the same show in the same city (0 = off, max 10).
          </span>
          {!poolValid && <span className="ai-inline-error">Enter a whole number from 0 to 10</span>}
        </div>
        <div className="ai-admin-row">
          <label>Daily pull</label>
          <input type="checkbox" checked={!!cfg.dailyPull} disabled={save.isPending || keyMissing || !cfg.configured}
            onChange={e => toggleDailyPull(e.target.checked)} />
          <span className="ai-admin-note">
            Refresh every event we hold in the background (sold ledger + stats): daily up to 60 days out, weekly beyond, at most
            10 every 15 min, standing aside whenever Compare is using TicketMetric. Saves at once.
          </span>
        </div>
        <div className="ai-admin-row" style={{ marginTop: 10 }}>
          <button type="submit" className="btn-primary" disabled={!dirty || !poolValid || save.isPending || keyMissing}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {dirty && (
            <button type="button" className="btn-secondary" onClick={() => { setEmail(null); setPassword(''); setProxyUrl(''); setPool(null); }}>Discard</button>
          )}
          <button type="button" className="btn-secondary" onClick={runTest} disabled={testing || !cfg.configured}
            title={cfg.configured ? 'Proxy exit IP, login, Pro plan, search and sold history (up to ~50 s)' : 'Save the email and password first'}>
            {testing ? 'Testing… (up to ~50 s)' : 'Test connection'}
          </button>
          {cfg.updatedAt && <span className="ai-admin-note">last saved {when(cfg.updatedAt)}</span>}
        </div>
      </form>

      {checks.length > 0 && (
        <div className="ai-admin-card" style={{ marginTop: 12 }}>
          <h4>
            {shownTest.ok ? 'Connection test passed' : 'Connection test found problems'}
            {shownTest.at ? <span className="ai-admin-note" style={{ textTransform: 'none', marginLeft: 8 }}>{when(shownTest.at)}{shownTest.durationMs ? ` · ${(shownTest.durationMs / 1000).toFixed(1)} s` : ''}</span> : null}
          </h4>
          <ul className="tm-check-list">
            {checks.map(c => (
              <li key={c.label} className={`tm-check-${c.state}`}>
                <span className="tm-check-mark">{MARK[c.state]}</span>
                <span className="tm-check-label">{c.label}</span>
                <span className="tm-check-text">{c.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h3 style={{ marginTop: 16 }}>Live status</h3>
      <div className="ai-admin-grid" style={{ marginTop: 8 }}>
        <div className="ai-admin-card">
          <h4>Connection</h4>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {!cfg.configured ? 'Not connected' : client.loggedIn ? 'Logged in' : 'Not logged in (logs in on the next call)'}
          </div>
          <div className="ai-admin-note">
            {client.proxied ? `via proxy ${client.proxy || ''}` : 'direct connection (no proxy)'}
            {client.tokenExpiresAt ? ` · token until ${when(client.tokenExpiresAt)}` : ''}
            {cfg.lastLoginAt ? ` · last login ${timeAgo(cfg.lastLoginAt)}` : ''}
          </div>
          {client.loginCooldownUntil && (
            <div className="ai-inline-error" style={{ marginTop: 4 }}>
              Login failed{client.loginError ? `: ${client.loginError}` : ''} — next try after {when(client.loginCooldownUntil)}
            </div>
          )}
          {cfg.lastError && !client.loginCooldownUntil && (
            <div className="ai-inline-error" style={{ marginTop: 4 }}>Last error: {cfg.lastError}</div>
          )}
        </div>
        <div className="ai-admin-card">
          <h4>Plan</h4>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {!plan ? 'Unknown — run Test connection' : plan.pro ? (plan.name || 'Pro') : <span className="tm-admin-blocked">No Pro plan</span>}
          </div>
          <div className="ai-admin-note">
            {plan?.expires ? `expires ${fmtExpiry(plan.expires)}` : ''}
            {plan?.checkedAt ? `${plan?.expires ? ' · ' : ''}checked ${timeAgo(plan.checkedAt)}` : ''}
          </div>
        </div>
        <div className="ai-admin-card">
          <h4>Queue</h4>
          <div className="big">{queue.queueLength ?? 0}</div>
          <div className="ai-admin-note">
            waiting · {queue.callsLastHour ?? 0} TicketMetric calls in the last hour
            {queue.inFlight ? <><br />running: {queue.inFlight.label || queue.inFlight.key}{queue.inFlight.phase ? ` (${queue.inFlight.phase})` : ''}</> : null}
          </div>
          {queue.pausedUntil && (
            <div className="tm-warn" style={{ marginTop: 6 }}>
              Paused until {when(queue.pausedUntil)}{queue.pauseReason ? ` — ${queue.pauseReason}` : ''}
            </div>
          )}
        </div>
        <div className="ai-admin-card">
          <h4>Daily pull</h4>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{dp?.enabled ? 'On' : 'Off'}</div>
          <div className="ai-admin-note">
            {dp?.lastTickAt ? `last tick ${timeAgo(dp.lastTickAt)}` : 'no tick yet'}
            {` · ${dp?.enqueuedToday ?? 0} queued today`}
            {dp?.skippedReason ? ` · skipped: ${SKIP_TEXT[dp.skippedReason] || dp.skippedReason}` : ''}
            {dp?.planCheckedAt ? ` · plan checked ${timeAgo(dp.planCheckedAt)}` : ''}
            {dp?.nextTickAt && dp?.enabled ? ` · next ${when(dp.nextTickAt)}` : ''}
          </div>
          {dp?.pausedReason && <div className="tm-warn" style={{ marginTop: 6 }}>Paused: {SKIP_TEXT[dp.pausedReason] || dp.pausedReason}</div>}
          {dp?.lastError && <div className="ai-inline-error" style={{ marginTop: 4 }}>Last error: {dp.lastError}</div>}
        </div>
      </div>

      {blocked.length > 0 && (
        <div className="message message-error" style={{ marginBottom: 12 }}>
          <b>Blocked endpoints</b> (refused right after a fresh login — usually the plan or the credentials):
          <ul style={{ margin: '6px 0 0 18px' }}>
            {blocked.map(b => (
              <li key={b.endpoint}>{b.endpoint} until {when(b.until)}{b.reason ? ` — ${b.reason}` : ''}</li>
            ))}
          </ul>
        </div>
      )}

      <h3 style={{ marginTop: 8 }}>Recent jobs</h3>
      {recent.length === 0 ? (
        <div className="ai-admin-note" style={{ marginTop: 6 }}>No TicketMetric jobs since the server started.</div>
      ) : (
        <div className="table-container" style={{ marginTop: 6 }}>
          <table>
            <thead>
              <tr><th>Job</th><th>Priority</th><th>Status</th><th>Queued</th><th>Took</th><th>Error</th></tr>
            </thead>
            <tbody>
              {recent.map((j, i) => (
                <tr key={`${j.key}-${j.finishedAt || i}`}>
                  <td title={j.key}>{j.label || j.key}</td>
                  <td>{PRIORITY_TEXT[j.priority] ?? j.priority}</td>
                  <td className={j.status === 'done' ? undefined : 'tm-admin-blocked'}>{j.status}</td>
                  <td>{when(j.enqueuedAt)}</td>
                  <td>{j.ms ? `${(j.ms / 1000).toFixed(1)} s` : '-'}</td>
                  <td style={{ whiteSpace: 'normal' }}>
                    {!j.error ? '' : typeof j.error === 'string' ? j.error : `${j.error.message || ''}${j.error.code ? ` (${j.error.code})` : ''}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TicketMetricAdminTab;
